import React, { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey } from "@mui/material/colors";
import SaveIcon from "@mui/icons-material/Save";
import { categoryDiscountSchema } from "../../app/schema";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { FormInputDropdown } from "../../components/form-components/FormInputDropdown";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
} from "../../redux/counterSlice";
import { parseArray } from "../../app/utlils";
import { FormAutoCompleteGroup } from "../../components/form-components/FormAutoCompleteGroup";
import { FormAutoComplete } from "../../components/form-components/FormAutoComplete";

function ProductDiscount() {
  const dispatch = useDispatch();
  const { regionList } = useSelector((state) => state.counter);
  const methods = useForm({
    resolver: yupResolver(categoryDiscountSchema),
  });
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = methods;

  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [discountList, setDiscountList] = useState([]);
  const [productList, setProductList] = useState([]);

  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Fetch only discount product list
  const fetchDiscountProductList = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllDiscountProductList");
      if (response?.data?.data?.length > 0) {
        setFormData(response.data.data);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
    }
  };

  // Fetch only discount list
  const fetchDiscountList = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getAllActiveDiscountList");
      if (response?.data?.data?.length > 0) {
        setDiscountList(response.data.data);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
    }
  };

  // Fetch only product list
  const fetchProductList = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/getActiveProductNoDiscount");
      if (response?.data?.data?.length > 0) {
        setProductList(response.data.data);
      }
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
    }
  };

  // Initial load: fetch all lists
  const loadInitialData = async () => {
    await Promise.all([
      fetchDiscountProductList(),
      fetchDiscountList(),
      fetchProductList(),
    ]);
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{+dataIndex + 1}</p>;
        },
      },
    },
    {
      name: "discount_name",
      label: "Product Discount Name",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{formData[dataIndex]?.discount?.[0]?.discount_name}</p>;
        },
      },
    },

    {
      name: "discount_percentage",
      label: "Percentage",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <p>{formData[dataIndex]?.discount?.[0]?.discount_percentage}%</p>
          );
        },
      },
    },
    {
      name: "products",
      label: "Products",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const products = formData[dataIndex]?.product || [];
          const maxPills = 4;
          const allNames = products.map((c) => c.itemName).join(", ");
          const pills = products.slice(0, maxPills).map((category, idx) => (
            <span
              key={category.id || category.itemName || idx}
              title={allNames}
              style={{
                display: "inline-block",
                background: "#e0e7ff",
                color: "#3730a3",
                borderRadius: "16px",
                padding: "2px 10px",
                marginRight: "4px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {category.itemName}
            </span>
          ));
          const extraCount = products.length - maxPills;
          return (
            <span>
              {pills}
              {extraCount > 0 && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#fca5a5",
                    color: "#991b1b",
                    borderRadius: "16px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  title={allNames}
                >
                  +{extraCount} more
                </span>
              )}
            </span>
          );
        },
      },
    },
    {
      name: "region_id",
      label: "Regions",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const regionIds = formData[dataIndex]?.discount?.[0]?.region_id || [];
          const maxPills = 4;

          const matchedRegions = regionIds
            .map((id) =>
              regionList?.find((r) => Number(r.region_id) === Number(id)),
            )
            .filter(Boolean);

          const allNames = matchedRegions.map((r) => r.city?.name).join(", ");

          const pills = matchedRegions.slice(0, maxPills).map((region, idx) => (
            <span
              key={region.region_id || idx}
              title={allNames}
              style={{
                display: "inline-block",
                background: "#dcfce7",
                color: "#166534",
                borderRadius: "16px",
                padding: "2px 10px",
                marginRight: "4px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {region.city?.name}
            </span>
          ));

          const extraCount = matchedRegions.length - maxPills;
          return (
            <span>
              {pills}
              {extraCount > 0 && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#fca5a5",
                    color: "#991b1b",
                    borderRadius: "16px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  title={allNames}
                >
                  +{extraCount} more
                </span>
              )}
            </span>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1 ? true : false}
              onChange={() => {
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () =>
                      toggleStatus(
                        formData[dataIndex]?.product_discount_id,
                        formData[dataIndex]?.active_status,
                      ),
                  }),
                );
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },

    {
      name: "Edit",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;

          return (
            <div className="actionBtns">
              <Button
                disabled={status === 1 ? true : false}
                aria-label="edit"
                onClick={() => editClickHandler(formData[dataIndex])}
              >
                <EditIcon
                  fontSize="medium"
                  sx={{ color: status === 1 ? grey[600] : "secondary" }}
                />
              </Button>
            </div>
          );
        },
      },
    },
    {
      name: "Delete",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;

          return (
            <div className="actionBtns">
              <Button
                disabled={status === 1 ? true : false}
                aria-label="edit"
                onClick={() => {
                  dispatch(
                    showConfirmModal({
                      msg: "Delete",
                      action: () =>
                        deleteClickHandler(
                          formData[dataIndex]?.product_discount_id,
                        ),
                    }),
                  );
                }}
              >
                <DeleteOutlineOutlinedIcon
                  color="error"
                  fontSize="medium"
                  sx={{ color: status === 1 ? grey[600] : "red" }}
                />
              </Button>
            </div>
          );
        },
      },
    },
  ];
  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };
  const editClickHandler = (item) => {
    setOpen(true);
    setSelectedRow({ ...item });
  };
  const onSubmit = (data) => {
    saveForm(data);
  };

  const toggleStatus = (id, status) => {
    setLoader(true);
    const toggleDiscountApi =
      status == 0 ? "/activateDiscountProduct/" : "/deActivateDiscountProduct/";
    axios
      .get(toggleDiscountApi + id)
      .then(function () {
        loadInitialData();
      })
      .catch(function (err) {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal());
      });
  };
  const saveForm = async (data) => {
    setLoader(true);
    try {
      let response;
      const payload = {
        product: parseArray(data?.product)?.toString(),
        discount_id: data?.discount_id,
      };

      if (selectedRow?.product_discount_id) {
        response = await axios.post("/updateDiscountProduct", {
          product_discount_id: selectedRow?.product_discount_id,
          ...payload,
        });
      } else {
        response = await axios.post("/createDiscountProduct", payload);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        setOpen(false);
        setSelectedRow(() => null);
        reset();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).map((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }

        dispatch(
          showModal({
            msg: errDesc,
            error: response?.data?.error,
          }),
        );
        setOpen(false);
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        }),
      );
      setOpen(false);
    } finally {
      setLoader(false);
    }
  };
  const deleteClickHandler = async (id) => {
    setLoader(true);
    try {
      let response;
      if (id) {
        response = await axios.get("/deleteDiscountProduct/" + id);
        if (!response?.data?.error) {
          toast.success(response?.data?.message);
          loadInitialData();
          setOpen(false);
          setSelectedRow(() => null);
          reset();
        } else {
          let errDesc = "";
          if (response?.data?.data) {
            Object.values(response?.data?.data).map((error, index) => {
              errDesc =
                index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
            });
          } else {
            errDesc = response?.data?.message;
          }

          dispatch(
            showModal({
              msg: errDesc,
              error: response?.data?.error,
            }),
          );
        }
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        }),
      );
    } finally {
      setLoader(false);
      setOpen(false);
    }
  };
  const CreateEditDialog = React.memo(() => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
        <DialogTitle className="text-primary">
          {selectedRow ? "Edit Product Discount" : "New Product Discount"}
        </DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={12}>
              <FormAutoCompleteGroup
                name="product"
                control={control}
                label="Product"
                dataArray={productList?.map((item) => ({
                  value: item?.id,
                  label: item?.product_name,
                  group: item?.sub_category?.sub_category_name,
                }))}
                errors={errors}
                defaultValue={selectedRow?.product?.map((item) => ({
                  value: item?.id,
                  label: item?.itemName,
                }))}
                mandatory="true"
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputDropdown
                name="discount_id"
                control={control}
                label="Discount"
                type="single"
                options={discountList?.map((item) => ({
                  id: item?.discount_id,
                  name: `${item?.discount_name} (${item?.discount_percentage}%)`,
                }))}
                errors={errors}
                defaultValue={selectedRow?.discount_id}
                mandatory="true"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            // disabled={!isValid}
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  const options = {
    selectableRows: false,
    filter: false,
    download: true,
    viewColumns: true,
    print: false,
  };
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Product Discount</h4>
          <MUIDataTable
            data={formData?.length > 0 ? formData : []}
            columns={columns}
            options={options}
            title={
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setSelectedRow(null);
                  setOpen(true);
                  reset();
                }}
                icon={<AddIcon />}
              >
                New Product Discount
              </Button>
            }
          />
        </>
      )}
      <CreateEditDialog />
    </>
  );
}

export default ProductDiscount;
